/**
 * F41 (Wave 2 Track D — admin) — tournament moderation detail page.
 *
 * Route: `/tournaments/:id`
 * Permission: `tournaments:read` gates the page render (route-level). The
 * Suspend / Unsuspend / Edit / Cancel / Force-refund actions are additionally
 * gated on `tournaments:moderate` — a read-only actor (support) sees the
 * read-only view with no action bar.
 *
 * Composition:
 *   - Header with lifecycle StatusChip + suspended/cancelled badge
 *   - Suspended / Cancelled Banner
 *   - Tabs: Overview / Divisions (per-division registeredCount) / Registrants
 *     (the real roster from GET /admin/tournaments/:id/registrants — each
 *     registration joined to its division + registrant identity) / Bracket
 *     (BracketView read from the public live endpoint) / Announcements (every
 *     organizer announcement, hidden ones stamped; a moderator hides one with
 *     an audited reason — FIX 5.7)
 *   - Lifecycle AuditLogTimeline (synthesised from the tournament's own
 *     published/suspended/cancelled fields — there is no admin audit endpoint)
 *   - Moderation action bar + dialogs (gated on `tournaments:moderate`)
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  type AuditEvent,
  Banner,
  BracketView,
  type BracketViewData as UiBracketViewData,
  Button,
  CapacityBadge,
  Card,
  CurrencyDisplay,
  EmptyState,
  Skeleton,
  StatusChip,
  Tab,
  TabList,
  TabPanel,
  Table,
  Tabs,
  TournamentStatusBadge,
  type TournamentStatusBadgeStatus,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type {
  AdminAnnouncementItem,
  BracketView as TournamentBracketView,
  DivisionDetail,
  MatchDetail,
  RegistrantRosterEntry,
  TournamentDetail,
  TournamentStatus,
} from '@bravemove/types';
import { adminTournamentsApi } from '../../api/tournaments';
import {
  CancelTournamentDialog,
  EditTournamentDialog,
  ForceRefundTournamentDialog,
  HideAnnouncementDialog,
  PublishTournamentDialog,
  SuspendTournamentDialog,
} from '../../components/tournaments/TournamentReasonDialogs';
import { MatchScoreDialog } from '../../components/tournaments/MatchScoreDialog';
import {
  GenerateBracketDialog,
  SeedDivisionDialog,
} from '../../components/tournaments/DivisionBracketDialogs';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';

/**
 * Map the canonical F41 `TournamentStatus` onto the `TournamentStatusBadge`
 * variants. The primitive has no `registration_closed` variant, so that one
 * falls back to a `StatusChip`. `registration_open` reuses the primitive's
 * `published` ("Reg open") treatment.
 */
function asTournamentBadgeStatus(status: TournamentStatus): TournamentStatusBadgeStatus | null {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'published':
    case 'registration_open':
      return 'published';
    case 'live':
      return 'live';
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    default:
      return null; // 'registration_closed' — no badge variant
  }
}

function formatDateTime(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminTournamentDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [detail, setDetail] = useState<TournamentDetail | null>(null);
  // rev-08 — the admin bracket read returns ONE BracketView PER DIVISION.
  const [bracket, setBracket] = useState<readonly TournamentBracketView[] | null>(null);
  const [bracketLoaded, setBracketLoaded] = useState(false);
  const [registrants, setRegistrants] = useState<readonly RegistrantRosterEntry[] | null>(null);
  // FIX 5.7 — the organizer's announcements (hidden ones included), lazy-loaded.
  const [announcements, setAnnouncements] = useState<readonly AdminAnnouncementItem[] | null>(null);
  const [hideTarget, setHideTarget] = useState<AdminAnnouncementItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [suspendOpen, setSuspendOpen] = useState(false);
  const [unsuspendOpen, setUnsuspendOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [forceRefundOpen, setForceRefundOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [unsuspending, setUnsuspending] = useState(false);
  // In-play control (rev-12 follow-up).
  const [scoreMatch, setScoreMatch] = useState<MatchDetail | null>(null);
  const [seedTarget, setSeedTarget] = useState<DivisionDetail | null>(null);
  const [generateTarget, setGenerateTarget] = useState<DivisionDetail | null>(null);

  const canModerate = auth.adminUser?.permissions.includes('tournaments:moderate') ?? false;
  // FIX 4.4 (F8) — publishing a draft on the organizer's behalf.
  const canPublish = auth.adminUser?.permissions.includes('tournaments:create') ?? false;
  const canEnterScores = auth.adminUser?.permissions.includes('tournaments:enter_scores') ?? false;
  const canManageBracket =
    auth.adminUser?.permissions.includes('tournaments:manage_bracket') ?? false;

  const activeTab = searchParams.get('tab') ?? 'overview';
  const setTab = (tab: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const d = await adminTournamentsApi.getTournament(id);
      if (d === null) {
        setNotFound(true);
      } else {
        setDetail(d);
      }
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  // Bracket loader — lazy on the first Bracket-tab open, re-run after every
  // in-play mutation (score / finalize / correct / seed / generate).
  const loadBracket = useCallback(async () => {
    if (!id) return;
    try {
      const b = await adminTournamentsApi.getBracket(id);
      setBracket(b);
    } catch {
      // Bracket is best-effort — the panel shows the no-bracket EmptyState.
    } finally {
      setBracketLoaded(true);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'bracket' || bracketLoaded) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadBracket();
  }, [activeTab, bracketLoaded, loadBracket]);

  // Lazy-load the registrant roster only when the Registrants tab is first opened.
  useEffect(() => {
    if (activeTab !== 'registrants' || !id || registrants !== null) return;
    void (async () => {
      try {
        const r = await adminTournamentsApi.getRegistrants(id);
        setRegistrants(r);
      } catch {
        setRegistrants([]);
      }
    })();
  }, [activeTab, id, registrants]);

  // Lazy-load the announcements only when the Announcements tab is first opened.
  useEffect(() => {
    if (activeTab !== 'announcements' || !id || announcements !== null) return;
    void (async () => {
      try {
        setAnnouncements(await adminTournamentsApi.listAnnouncements(id));
      } catch {
        setAnnouncements([]);
      }
    })();
  }, [activeTab, id, announcements]);

  const handleHideAnnouncement = async (reason: string): Promise<void> => {
    if (!id || !hideTarget) return;
    setActionError(null);
    try {
      await adminTournamentsApi.hideAnnouncement(hideTarget.id, reason);
      setAnnouncements(await adminTournamentsApi.listAnnouncements(id));
      setHideTarget(null);
      toast.push({
        tone: 'success',
        title: t('admin:tournament.hideAnnouncementDialog.done', {
          defaultValue: 'Announcement hidden',
        }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  const handleSuspend = async (reason: string): Promise<void> => {
    if (!id) return;
    setActionError(null);
    try {
      await adminTournamentsApi.suspendTournament(id, { reason });
      await refresh();
      setSuspendOpen(false);
      toast.push({
        tone: 'success',
        title: t('tournament:action.suspend', { defaultValue: 'Suspend' }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  const handleUnsuspend = async (): Promise<void> => {
    if (!id) return;
    setActionError(null);
    setUnsuspending(true);
    try {
      await adminTournamentsApi.unsuspendTournament(id);
      await refresh();
      setUnsuspendOpen(false);
      toast.push({
        tone: 'success',
        title: t('tournament:action.reinstate', { defaultValue: 'Unsuspend' }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    } finally {
      setUnsuspending(false);
    }
  };

  const handlePublish = async (reason: string): Promise<void> => {
    if (!id) return;
    setActionError(null);
    try {
      await adminTournamentsApi.publishTournament(id, { reason });
      await refresh();
      setPublishOpen(false);
      toast.push({
        tone: 'success',
        title: t('admin:tournament.publishDialog.done', { defaultValue: 'Tournament published' }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  const handleCancel = async (reason: string): Promise<void> => {
    if (!id) return;
    setActionError(null);
    try {
      await adminTournamentsApi.cancelTournament(id, { reason });
      await refresh();
      setCancelOpen(false);
      toast.push({
        tone: 'success',
        title: t('tournament:action.cancel', { defaultValue: 'Cancel tournament' }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  const handleEdit = async (input: {
    title: string;
    description: string | null;
  }): Promise<void> => {
    if (!id) return;
    setActionError(null);
    try {
      await adminTournamentsApi.editTournament(id, input);
      await refresh();
      setEditOpen(false);
      toast.push({
        tone: 'success',
        title: t('tournament:action.edit', { defaultValue: 'Edit tournament' }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  const handleForceRefund = async (reason: string): Promise<void> => {
    if (!id) return;
    setActionError(null);
    try {
      const res = await adminTournamentsApi.forceRefund(id, { reason });
      setForceRefundOpen(false);
      toast.push({
        tone: 'success',
        title: t('admin:tournament.forceRefundDialog.doneCount', {
          defaultValue: 'Flagged {count} paid registration(s) for refund.',
          count: res.refundedCount,
        }),
      });
    } catch (err) {
      setActionError(translateApiError(err, t));
    }
  };

  // Synthesise a lifecycle timeline from the tournament's own fields (there is
  // no admin audit endpoint for tournaments at MVP — the open-games precedent).
  const auditEvents = useMemo<AuditEvent[]>(() => {
    if (!detail) return [];
    const events: AuditEvent[] = [];
    events.push({
      id: 'created',
      at: detail.createdAt,
      kind: 'create',
      summary: t('tournament:status.draft', { defaultValue: 'Draft' }),
      actor: { name: detail.createdByUserId.slice(0, 8) },
    });
    if (detail.publishedAt) {
      events.push({
        id: 'published',
        at: detail.publishedAt,
        kind: 'info',
        summary: t('tournament:status.published', { defaultValue: 'Published' }),
        actor: { name: detail.createdByUserId.slice(0, 8) },
      });
    }
    if (detail.suspendedAt) {
      events.push({
        id: 'suspended',
        at: detail.suspendedAt,
        kind: 'alert',
        summary: t('admin:tournament.list.suspendedBadge', { defaultValue: 'Suspended' }),
        actor: { name: detail.suspendedByAdminId?.slice(0, 8) ?? 'admin' },
        ...(detail.suspensionReason ? { details: detail.suspensionReason } : {}),
      });
    }
    if (detail.cancelledAt) {
      events.push({
        id: 'cancelled',
        at: detail.cancelledAt,
        kind: 'delete',
        summary: t('tournament:status.cancelled', { defaultValue: 'Cancelled' }),
        actor: { name: 'admin' },
        ...(detail.cancellationReason ? { details: detail.cancellationReason } : {}),
      });
    }
    return events.sort((a, b) => b.at.localeCompare(a.at));
  }, [detail, t]);

  const divisionColumns = useMemo<readonly TableColumn<DivisionDetail>[]>(
    () => [
      {
        key: 'name',
        header: t('admin:tournament.detail.divisions', { defaultValue: 'Divisions' }),
        cell: (row) => <span className="text-[13px] text-ink">{row.name}</span>,
      },
      {
        key: 'kind',
        header: t('admin:tournament.detail.format', { defaultValue: 'Format' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {t(`tournament:registration_kind.${row.registrationKind}`, {
              defaultValue: row.registrationKind,
            })}{' '}
            · {t(`tournament:format.${row.format}`, { defaultValue: row.format })}
          </span>
        ),
      },
      {
        key: 'registered',
        header: t('admin:tournament.detail.registeredInDivision', { defaultValue: 'Registered' }),
        align: 'end',
        cell: (row) => (
          <div className="flex items-center justify-end gap-2">
            <span className="text-[12.5px] font-mono tabular-nums text-ink-2">
              {row.registeredCount}/{row.capacity}
            </span>
            <CapacityBadge filled={row.registeredCount} total={row.capacity} size="sm" />
          </div>
        ),
      },
      {
        key: 'fee',
        header: t('admin:tournament.detail.listingFee', { defaultValue: 'Fee' }),
        align: 'end',
        cell: (row) =>
          row.feeMinor > 0 ? (
            <CurrencyDisplay amount={row.feeMinor} currency={row.feeCurrency} locale={locale} />
          ) : (
            <span className="text-[12px] text-ink-3">—</span>
          ),
      },
      // In-play bracket management (rev-12 follow-up) — gated on
      // `tournaments:manage_bracket`; the server re-enforces.
      ...(canManageBracket
        ? ([
            {
              key: 'bracketActions',
              header: t('admin:tournament.detail.bracketActions', { defaultValue: 'Bracket' }),
              align: 'end',
              cell: (row) => (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSeedTarget(row)}
                    data-testid={`division-seed-${row.id}`}
                  >
                    {t('admin:tournament.detail.seed', { defaultValue: 'Seed' })}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setGenerateTarget(row)}
                    data-testid={`division-generate-${row.id}`}
                  >
                    {t('admin:tournament.detail.generateBracket', {
                      defaultValue: 'Generate bracket',
                    })}
                  </Button>
                </div>
              ),
            },
          ] as const satisfies readonly TableColumn<DivisionDetail>[])
        : []),
    ],
    [t, locale, canManageBracket],
  );

  const registrantColumns = useMemo<readonly TableColumn<RegistrantRosterEntry>[]>(
    () => [
      {
        key: 'registrant',
        header: t('admin:tournament.detail.registrant', { defaultValue: 'Registrant' }),
        cell: (row) => (
          <div className="flex flex-col">
            <span className="text-[13px] text-ink">
              {row.registrantTeamName ??
                row.registrantUserDisplayName ??
                t('admin:tournament.detail.unknownRegistrant', { defaultValue: 'Unknown' })}
            </span>
            {row.registrantUserEmail ? (
              <span className="text-[11px] text-ink-3">{row.registrantUserEmail}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'division',
        header: t('admin:tournament.detail.divisions', { defaultValue: 'Division' }),
        cell: (row) => <span className="text-[12px] text-ink-2">{row.divisionName ?? '—'}</span>,
      },
      {
        key: 'status',
        header: t('admin:tournament.detail.status', { defaultValue: 'Status' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {t(`tournament:registration_status.${row.status}`, { defaultValue: row.status })}
          </span>
        ),
      },
      {
        key: 'payment',
        header: t('admin:tournament.detail.payment', { defaultValue: 'Payment' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {t(`tournament:payment_status.${row.paymentStatus}`, {
              defaultValue: row.paymentStatus,
            })}
          </span>
        ),
      },
      {
        key: 'fee',
        header: t('admin:tournament.detail.listingFee', { defaultValue: 'Fee' }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.netFeeAmountMinor} currency="USD" locale={locale} />
        ),
      },
    ],
    [t, locale],
  );

  if (loading) {
    return (
      <div className="space-y-4" data-testid="tournament-detail-loading">
        <Skeleton className="h-7 w-1/3 rounded-md" />
        <Skeleton className="h-28 w-full rounded-lg" />
      </div>
    );
  }

  if (notFound) {
    return (
      <EmptyState
        title={t('admin:tournament.detail.notFound', {
          defaultValue: 'This tournament no longer exists.',
        })}
      />
    );
  }

  if (error) return <Banner tone="danger">{error}</Banner>;
  if (!detail) return null;

  const isSuspended = detail.suspendedAt !== null;
  const isCancelled = detail.status === 'cancelled';
  const totalRegistered = detail.divisions.reduce((acc, d) => acc + d.registeredCount, 0);

  return (
    <div className="space-y-6" data-testid="tournament-detail-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-h1 font-bold text-ink">{detail.title}</h1>
          <p className="text-[12px] text-ink-3 mt-0.5 font-mono">{detail.id}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {(() => {
            const badgeStatus = asTournamentBadgeStatus(detail.status);
            return badgeStatus ? (
              <TournamentStatusBadge
                status={badgeStatus}
                label={t(`tournament:status.${detail.status}`)}
              />
            ) : (
              <StatusChip status="submitted" label={t(`tournament:status.${detail.status}`)} />
            );
          })()}
          {isSuspended ? (
            <TournamentStatusBadge
              status="suspended_by_admin"
              label={t('admin:tournament.list.suspendedBadge', { defaultValue: 'Suspended' })}
              data-testid="tournament-detail-suspended-badge"
            />
          ) : null}
        </div>
      </div>

      {isSuspended ? (
        <div data-testid="tournament-suspended-banner">
          <Banner tone="warning">
            {t('admin:tournament.detail.suspendedBanner', {
              defaultValue:
                'This tournament is suspended — hidden from the marketplace. Data is retained.',
            })}
          </Banner>
        </div>
      ) : null}
      {isCancelled ? (
        <div data-testid="tournament-cancelled-banner">
          <Banner tone="danger">
            {t('admin:tournament.detail.cancelledBanner', {
              defaultValue: 'This tournament is cancelled.',
            })}
          </Banner>
        </div>
      ) : null}

      <Tabs value={activeTab} defaultValue="overview" onValueChange={setTab}>
        <TabList>
          <Tab value="overview">
            {t('admin:tournament.detail.overview', { defaultValue: 'Overview' })}
          </Tab>
          <Tab value="divisions">
            {t('admin:tournament.detail.divisions', { defaultValue: 'Divisions' })}
          </Tab>
          <Tab value="registrants">
            {t('admin:tournament.detail.registrants', { defaultValue: 'Registrants' })}
          </Tab>
          <Tab value="bracket">
            {t('admin:tournament.detail.bracket', { defaultValue: 'Bracket' })}
          </Tab>
          <Tab value="announcements">
            {t('admin:tournament.detail.announcements', { defaultValue: 'Announcements' })}
          </Tab>
        </TabList>

        <TabPanel value="overview">
          <Card>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2 text-[12.5px]">
              <Row label={t('admin:tournament.detail.sport', { defaultValue: 'Sport' })}>
                <span className="capitalize">{detail.sportName.replace(/-/g, ' ')}</span>
              </Row>
              <Row label={t('admin:tournament.detail.format', { defaultValue: 'Format' })}>
                {t(`tournament:format.${detail.format}`, { defaultValue: detail.format })}
              </Row>
              <Row label={t('admin:tournament.detail.organizer', { defaultValue: 'Organizer' })}>
                <span className="font-mono text-[11px]">{detail.organizerB2bAccountId}</span>
              </Row>
              <Row label={t('admin:tournament.detail.schedule', { defaultValue: 'Schedule' })}>
                {formatDateTime(detail.startDate, locale)} →{' '}
                {formatDateTime(detail.endDate, locale)}
              </Row>
              <Row
                label={t('admin:tournament.detail.registration', { defaultValue: 'Registration' })}
              >
                {formatDateTime(detail.registrationOpenAt, locale)} →{' '}
                {formatDateTime(detail.registrationCloseAt, locale)}
              </Row>
              <Row
                label={t('admin:tournament.detail.totalRegistered', {
                  defaultValue: 'Total registered',
                })}
              >
                <span className="font-mono tabular-nums">
                  {totalRegistered}
                  {detail.totalCapacity !== null ? `/${detail.totalCapacity}` : ''}
                </span>
              </Row>
              <Row label={t('admin:tournament.detail.listingFee', { defaultValue: 'Listing fee' })}>
                <CurrencyDisplay
                  amount={detail.listingFeeMinor}
                  currency={detail.listingFeeCurrency}
                  locale={locale}
                />
              </Row>
              <Row
                label={t('admin:tournament.detail.tierAtPublish', {
                  defaultValue: 'Tier at publish',
                })}
              >
                {detail.subscriptionTierCodeAtPublish ?? '—'}
              </Row>
              <Row label={t('admin:tournament.detail.inviteOnly', { defaultValue: 'Invite-only' })}>
                {detail.inviteOnly
                  ? t('admin:tournament.detail.yes', { defaultValue: 'Yes' })
                  : t('admin:tournament.detail.no', { defaultValue: 'No' })}
              </Row>
            </dl>
            {detail.description ? (
              <p className="mt-4 text-[12.5px] text-ink-2 leading-snug">{detail.description}</p>
            ) : null}
          </Card>

          {/* Lifecycle timeline — synthesised from the tournament's own fields. */}
          <section className="mt-6 space-y-3" data-testid="tournament-audit-timeline">
            <h2 className="text-[13px] font-semibold text-ink-2 uppercase tracking-wide">
              {t('admin:tournament.detail.actions', { defaultValue: 'Moderation' })}
            </h2>
            <AuditLogTimeline events={auditEvents} />
          </section>
        </TabPanel>

        <TabPanel value="divisions">
          {detail.divisions.length === 0 ? (
            <EmptyState
              title={t('admin:tournament.detail.noDivisions', {
                defaultValue: 'No divisions configured.',
              })}
            />
          ) : (
            <Table
              data={detail.divisions as DivisionDetail[]}
              columns={divisionColumns as TableColumn<DivisionDetail>[]}
              rowKey={(row) => row.id}
            />
          )}
        </TabPanel>

        <TabPanel value="registrants">
          {registrants === null ? (
            <div className="py-8" data-testid="tournament-registrants-loading">
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>
          ) : registrants.length === 0 ? (
            <div data-testid="tournament-no-registrants">
              <EmptyState
                title={t('admin:tournament.detail.noRegistrants', {
                  defaultValue: 'No registrations yet.',
                })}
              />
            </div>
          ) : (
            <div data-testid="tournament-registrants">
              <Table
                data={registrants as RegistrantRosterEntry[]}
                columns={registrantColumns as TableColumn<RegistrantRosterEntry>[]}
                rowKey={(row) => row.id}
              />
            </div>
          )}
        </TabPanel>

        <TabPanel value="bracket">
          {bracket === null || bracket.length === 0 ? (
            <EmptyState
              title={t('admin:tournament.detail.noBracket', {
                defaultValue: 'No bracket has been generated for this tournament yet.',
              })}
              data-testid="tournament-no-bracket"
            />
          ) : (
            <div className="space-y-8" data-testid="tournament-bracket">
              {/* rev-08 — the live read returns ONE BracketView PER DIVISION;
                  render one tree per division with its name as the header. */}
              {bracket.map((view) => {
                const division = detail.divisions.find((d) => d.id === view.divisionId);
                return (
                  <section
                    key={view.divisionId}
                    className="space-y-3"
                    data-testid={`tournament-bracket-${view.divisionId}`}
                  >
                    <h2 className="text-[13px] font-semibold text-ink-2 uppercase tracking-wide">
                      {division?.name ??
                        t('admin:tournament.detail.divisionFallback', {
                          defaultValue: 'Division',
                        })}
                    </h2>
                    {/* The @bravemove/types `BracketView` shape is structurally
                        compatible with the ui-web component's local
                        `BracketViewData` (the component mirrors the types
                        intentionally; `exactOptionalPropertyTypes` surfaces a
                        benign `winnerSide` optionality difference). Cast at the
                        seam — the component renders defensively. */}
                    <BracketView
                      bracket={view as unknown as UiBracketViewData}
                      onMatchPress={
                        canEnterScores
                          ? (m) => setScoreMatch(m as unknown as MatchDetail)
                          : undefined
                      }
                    />
                  </section>
                );
              })}
            </div>
          )}
        </TabPanel>

        <TabPanel value="announcements">
          {announcements === null ? (
            <div className="py-8" data-testid="tournament-announcements-loading">
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>
          ) : announcements.length === 0 ? (
            <div data-testid="tournament-no-announcements">
              <EmptyState
                title={t('admin:tournament.detail.noAnnouncements', {
                  defaultValue: 'The organizer has not posted any announcements.',
                })}
              />
            </div>
          ) : (
            <ul className="space-y-3" data-testid="tournament-announcements">
              {announcements.map((a) => (
                <li key={a.id} data-testid={`tournament-announcement-${a.id}`}>
                  <Card>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 space-y-1">
                        <p className="text-[13px] font-semibold text-ink">{a.title}</p>
                        <p className="text-[11px] text-ink-3">
                          {formatDateTime(a.createdAt, locale)} ·{' '}
                          {t(`tournament:announcement_audience.${a.audience}`, {
                            defaultValue: a.audience,
                          })}
                        </p>
                        <p className="text-[12.5px] text-ink-2 leading-snug whitespace-pre-line">
                          {a.body}
                        </p>
                        {a.hiddenAt ? (
                          <p
                            className="text-[12px] text-danger"
                            data-testid={`tournament-announcement-hidden-${a.id}`}
                          >
                            {a.hiddenReason
                              ? t('admin:tournament.detail.announcementHiddenReason', {
                                  defaultValue: 'Hidden: {reason}',
                                  reason: a.hiddenReason,
                                })
                              : t('admin:tournament.detail.announcementHiddenBadge', {
                                  defaultValue: 'Hidden',
                                })}
                          </p>
                        ) : null}
                      </div>
                      {canModerate && !a.hiddenAt ? (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setActionError(null);
                            setHideTarget(a);
                          }}
                          data-testid={`tournament-announcement-hide-${a.id}`}
                        >
                          {t('admin:tournament.detail.hideAnnouncement', { defaultValue: 'Hide' })}
                        </Button>
                      ) : null}
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </TabPanel>
      </Tabs>

      {/* Moderation action bar — gated on tournaments:moderate */}
      {canModerate ? (
        <div className="flex flex-col gap-2" data-testid="tournament-action-bar">
          {actionError ? <Banner tone="danger">{actionError}</Banner> : null}
          <div className="flex flex-wrap justify-end gap-2">
            {canPublish && detail.status === 'draft' ? (
              <Button
                variant="primary"
                onClick={() => setPublishOpen(true)}
                data-testid="tournament-publish"
              >
                {t('admin:tournament.publishDialog.confirm', { defaultValue: 'Publish' })}
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setEditOpen(true)} data-testid="tournament-edit">
              {t('tournament:action.edit', { defaultValue: 'Edit tournament' })}
            </Button>
            {!isCancelled ? (
              <Button
                variant="destructive"
                onClick={() => setForceRefundOpen(true)}
                data-testid="tournament-force-refund"
              >
                {t('tournament:action.force_refund', { defaultValue: 'Force refund' })}
              </Button>
            ) : null}
            {!isCancelled ? (
              <Button
                variant="destructive"
                onClick={() => setCancelOpen(true)}
                data-testid="tournament-cancel"
              >
                {t('tournament:action.cancel', { defaultValue: 'Cancel tournament' })}
              </Button>
            ) : null}
            {isSuspended ? (
              <Button
                variant="primary"
                onClick={() => setUnsuspendOpen(true)}
                data-testid="tournament-unsuspend"
              >
                {t('tournament:action.reinstate', { defaultValue: 'Unsuspend' })}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => setSuspendOpen(true)}
                data-testid="tournament-suspend"
              >
                {t('tournament:action.suspend', { defaultValue: 'Suspend' })}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[12px] text-ink-3" data-testid="tournament-readonly-note">
          {t('admin:tournament.detail.readOnlyNote', {
            defaultValue:
              'You have read-only access. Moderation actions require the moderate permission.',
          })}
        </p>
      )}

      <button
        type="button"
        className="text-[12px] text-accent hover:underline"
        onClick={() => navigate('/tournaments')}
      >
        {t('admin:tournament.list.title', { defaultValue: 'Tournaments' })}
      </button>

      <SuspendTournamentDialog
        open={suspendOpen}
        onSuspend={handleSuspend}
        onClose={() => setSuspendOpen(false)}
        errorMessage={suspendOpen ? actionError : null}
      />
      <PublishTournamentDialog
        open={publishOpen}
        onPublish={handlePublish}
        onClose={() => setPublishOpen(false)}
        errorMessage={publishOpen ? actionError : null}
      />
      <CancelTournamentDialog
        open={cancelOpen}
        onCancel={handleCancel}
        onClose={() => setCancelOpen(false)}
        errorMessage={cancelOpen ? actionError : null}
      />
      <ForceRefundTournamentDialog
        open={forceRefundOpen}
        onForceRefund={handleForceRefund}
        onClose={() => setForceRefundOpen(false)}
        errorMessage={forceRefundOpen ? actionError : null}
      />
      <HideAnnouncementDialog
        open={hideTarget !== null}
        announcementTitle={hideTarget?.title ?? ''}
        onHide={handleHideAnnouncement}
        onClose={() => setHideTarget(null)}
        errorMessage={hideTarget ? actionError : null}
      />
      <EditTournamentDialog
        open={editOpen}
        initialTitle={detail.title}
        initialDescription={detail.description}
        onSave={handleEdit}
        onClose={() => setEditOpen(false)}
        errorMessage={editOpen ? actionError : null}
      />

      {/* In-play control (rev-12 follow-up) — score entry from the Bracket tab
          (`tournaments:enter_scores`) + seed / generate from the Divisions tab
          (`tournaments:manage_bracket`). The server re-enforces RBAC. */}
      <MatchScoreDialog
        open={scoreMatch !== null}
        tournamentId={id ?? ''}
        match={scoreMatch}
        onClose={() => setScoreMatch(null)}
        onSaved={async () => {
          await loadBracket();
          toast.push({
            tone: 'success',
            title: t('admin:tournament.scoreDialog.saved', { defaultValue: 'Score saved.' }),
          });
        }}
      />
      {seedTarget ? (
        <SeedDivisionDialog
          open={seedTarget !== null}
          tournamentId={id ?? ''}
          divisionId={seedTarget.id}
          divisionName={seedTarget.name}
          onClose={() => setSeedTarget(null)}
          onDone={async () => {
            await loadBracket();
            toast.push({
              tone: 'success',
              title: t('admin:tournament.seedDialog.done', { defaultValue: 'Division seeded.' }),
            });
          }}
        />
      ) : null}
      {generateTarget ? (
        <GenerateBracketDialog
          open={generateTarget !== null}
          tournamentId={id ?? ''}
          divisionId={generateTarget.id}
          divisionName={generateTarget.name}
          onClose={() => setGenerateTarget(null)}
          onDone={async () => {
            await loadBracket();
            await refresh();
            toast.push({
              tone: 'success',
              title: t('admin:tournament.generateDialog.done', {
                defaultValue: 'Bracket generated.',
              }),
            });
          }}
        />
      ) : null}

      {unsuspendOpen ? (
        <UnsuspendConfirm
          loading={unsuspending}
          onConfirm={() => void handleUnsuspend()}
          onClose={() => setUnsuspendOpen(false)}
        />
      ) : null}
    </div>
  );
}

function Row({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-3">{label}</dt>
      <dd className="text-ink-2 text-right">{children}</dd>
    </div>
  );
}

/** Small inline confirmation for the unsuspend action (no reason required). */
function UnsuspendConfirm({
  loading,
  onConfirm,
  onClose,
}: {
  readonly loading: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="tournament-unsuspend-dialog"
    >
      <div className="w-full max-w-sm rounded-lg bg-bg p-5 shadow-lg">
        <h2 className="text-[15px] font-semibold text-ink">
          {t('admin:tournament.unsuspendDialog.title', {
            defaultValue: 'Unsuspend this tournament?',
          })}
        </h2>
        <p className="mt-2 text-[13px] text-ink-2">
          {t('admin:tournament.unsuspendDialog.body', {
            defaultValue: 'It will become visible in the marketplace again.',
          })}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('admin:tournament.unsuspendDialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={loading}
            data-testid="tournament-confirm-unsuspend"
          >
            {t('admin:tournament.unsuspendDialog.confirm', { defaultValue: 'Unsuspend' })}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminTournamentDetailPage;
